/**
 * TrainingScheduleField
 * ----------------------
 * Compact, preset-driven training-schedule editor.
 *
 * Replaces the legacy three-row schedule UI (time window + frequency
 * in seconds + day toggle) with a single chip selector for the cases
 * ~95% of users actually want: Run once / Hourly / Daily / Weekly /
 * Custom. The "Custom" mode opens an inline editor with a
 * human-friendly frequency input ("Run every 4 hours") and an
 * optional time-of-day restriction that stays hidden until the user
 * explicitly opts in.
 *
 * Backend contract is unchanged: this component reads + writes the
 * existing ``{ start_time, end_time, days, frequency }`` shape, where
 * ``frequency`` is a string of whole seconds and ``days`` is an array
 * of full English day names. "Run once" writes an empty ``days``
 * array — the dispatcher never schedule-fires such a pipeline, and
 * the create endpoint queues exactly one immediate run for it.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Button, Stack, Typography, IconButton, Tooltip,
  Select, MenuItem, FormControl, OutlinedInput, Switch,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
// Plain date-fns on purpose: the TimePicker round-trip works on local
// wall-clock Dates (not instants), so the timezone-aware format from
// utils/datetime would corrupt the picked time.
import { format, parse } from 'date-fns';
import InfoIcon from '@mui/icons-material/Info';

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DEFAULT_START = '00:00:00';
const DEFAULT_END = '23:59:59';

const MODES = [
  { id: 'once',    label: 'Run once' },
  { id: 'hourly',  label: 'Hourly'  },
  { id: 'daily',   label: 'Daily'   },
  { id: 'weekly',  label: 'Weekly'  },
  { id: 'custom',  label: 'Custom'  },
];

// ---- Frequency / unit helpers ------------------------------------------
// We accept whole-second integers in storage but ask the user for a
// number + unit (minutes / hours / days). When the value happens to be
// a clean multiple of the chosen unit we reverse it cleanly; otherwise
// we fall back to minutes so we never lose precision in the round
// trip.
const UNIT_TO_SECONDS = { minutes: 60, hours: 3600, days: 86400 };

function secondsToCount(secondsRaw, unit) {
  const total = parseInt(secondsRaw, 10);
  if (!Number.isFinite(total) || total <= 0) return 1;
  const div = UNIT_TO_SECONDS[unit] || 1;
  return Math.max(1, Math.round(total / div));
}

function pickBestUnit(secondsRaw) {
  const total = parseInt(secondsRaw, 10);
  if (!Number.isFinite(total) || total <= 0) return 'hours';
  if (total % UNIT_TO_SECONDS.days === 0) return 'days';
  if (total % UNIT_TO_SECONDS.hours === 0) return 'hours';
  return 'minutes';
}

// ---- Mode <-> schedule mapping -----------------------------------------

function isFullDay({ start_time, end_time }) {
  return (start_time === DEFAULT_START || !start_time)
      && (end_time === DEFAULT_END || !end_time);
}

function deriveMode(value) {
  if (!value) return 'daily';
  const freq = parseInt(value.frequency, 10);
  const days = Array.isArray(value.days) ? value.days : [];
  const all7  = days.length === 7;
  const fullDay = isFullDay(value);

  // No selected days = the scheduler never fires = "Run once".
  if (days.length === 0) return 'once';
  if (all7 && fullDay && freq === 3600) return 'hourly';
  if (all7 && fullDay && freq === 86400) return 'daily';
  // Weekly = once-a-day cadence on a single weekday with no time-of-day
  // restriction. We accept any single-day selection so editing a
  // weekly schedule and just changing the day stays in "Weekly" mode
  // rather than flipping to Custom.
  if (days.length === 1 && fullDay && freq === 86400) return 'weekly';
  return 'custom';
}

function buildPresetSchedule(mode, prev) {
  const base = {
    start_time: DEFAULT_START,
    end_time:   DEFAULT_END,
    days:       [...ALL_DAYS],
    frequency:  '86400',
  };
  if (mode === 'once')   return { ...base, days: [] };
  if (mode === 'hourly') return { ...base, frequency: '3600' };
  if (mode === 'daily')  return base;
  if (mode === 'weekly') {
    // Preserve the user's existing single-day pick if they had one,
    // otherwise default to Monday.
    const prevSingle = (prev?.days?.length === 1) ? prev.days : ['Monday'];
    return { ...base, days: prevSingle };
  }
  return prev || base;
}

// ---- Human-readable summary --------------------------------------------

function describe(value, mode) {
  if (!value) return '';
  const days = Array.isArray(value.days) ? value.days : [];
  const restricted = !isFullDay(value);
  const window = restricted
    ? ` between ${value.start_time?.slice(0, 5) || '00:00'} and ${value.end_time?.slice(0, 5) || '23:59'}`
    : '';

  if (mode === 'once')   return 'Trains once right away, then only on manual runs or data triggers.';
  if (mode === 'hourly') return `Trains every hour, every day${window}.`;
  if (mode === 'daily')  return `Trains once a day, every day${window}.`;
  if (mode === 'weekly') {
    const d = days[0] || 'Monday';
    return `Trains once a week, on ${d}${window}.`;
  }

  // Custom — show frequency in human terms.
  const freqSec = parseInt(value.frequency, 10) || 0;
  const u = pickBestUnit(freqSec);
  const n = secondsToCount(freqSec, u);
  const unitLbl = n === 1 ? u.replace(/s$/, '') : u;
  const dayPart = days.length === 0
    ? 'no days selected'
    : days.length === 7
      ? 'every day'
      : days.length <= 3
        ? `on ${days.map(d => d.slice(0, 3)).join(', ')}`
        : `${days.length} days a week`;
  return `Runs every ${n} ${unitLbl}, ${dayPart}${window}.`;
}

// ---- Component ---------------------------------------------------------

const DAY_LETTERS = { Monday: 'M', Tuesday: 'T', Wednesday: 'W', Thursday: 'T', Friday: 'F', Saturday: 'S', Sunday: 'S' };

export default function TrainingScheduleField({
  value,
  onChange,
  helpText = 'When the model trains automatically. Pick a preset or set your own cadence.',
  // When "Run once" is promoted to a page-level trigger mode (run once /
  // schedule / data-driven), the pages hide the in-component chip so the
  // same concept isn't offered in two places.
  hideOnce = false,
  title = 'Training schedule',
}) {
  // Lazy-initialize mode from the incoming value so editing an
  // existing schedule keeps the right preset highlighted.
  const [mode, setMode] = useState(() => {
    const derived = deriveMode(value);
    return (hideOnce && derived === 'once') ? 'daily' : derived;
  });
  const [restrictHours, setRestrictHours] = useState(() => !isFullDay(value || {}));

  // Frequency unit (only meaningful when mode === 'custom'). We pick
  // a sane unit on first render based on the existing seconds value.
  const [freqUnit, setFreqUnit] = useState(() => pickBestUnit(value?.frequency));

  const days = Array.isArray(value?.days) ? value.days : [];

  // ---- Handlers --------------------------------------------------------
  const setMany = useCallback((patch) => {
    onChange({ ...(value || {}), ...patch });
  }, [onChange, value]);

  const handleModeClick = useCallback((nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    if (nextMode === 'custom') {
      // Stay on whatever the user already had so they can edit it
      // freely. Make sure restrictHours reflects current state.
      setRestrictHours(!isFullDay(value || {}));
      setFreqUnit(pickBestUnit(value?.frequency));
      return;
    }
    setRestrictHours(false);
    onChange(buildPresetSchedule(nextMode, value));
  }, [mode, onChange, value]);

  const handleFreqCountChange = useCallback((e) => {
    const count = Math.max(1, parseInt(e.target.value, 10) || 1);
    const seconds = count * (UNIT_TO_SECONDS[freqUnit] || 60);
    setMany({ frequency: String(Math.max(60, seconds)) });
  }, [freqUnit, setMany]);

  const handleFreqUnitChange = useCallback((e) => {
    const nextUnit = e.target.value;
    const count = secondsToCount(value?.frequency, freqUnit);
    setFreqUnit(nextUnit);
    const seconds = count * (UNIT_TO_SECONDS[nextUnit] || 60);
    setMany({ frequency: String(Math.max(60, seconds)) });
  }, [freqUnit, setMany, value]);

  const handleDayToggle = useCallback((day) => {
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    // Preserve canonical Mon..Sun order so the summary reads naturally.
    next.sort((a, b) => ALL_DAYS.indexOf(a) - ALL_DAYS.indexOf(b));
    setMany({ days: next });
  }, [days, setMany]);

  const handleRestrictToggle = useCallback((e) => {
    const on = e.target.checked;
    setRestrictHours(on);
    if (!on) {
      setMany({ start_time: DEFAULT_START, end_time: DEFAULT_END });
    }
  }, [setMany]);

  const handleTimeChange = useCallback((field, dt) => {
    if (!dt) return;
    setMany({ [field]: format(dt, 'HH:mm:ss') });
  }, [setMany]);

  // ---- Derived render data --------------------------------------------
  const summary = useMemo(() => describe(value, mode), [value, mode]);
  const freqCount = useMemo(
    () => secondsToCount(value?.frequency, freqUnit),
    [value, freqUnit],
  );
  const parseTime = (s) => (s ? parse(s, 'HH:mm:ss', new Date()) : null);

  return (
    <Box>
      {/* Single header row: title + preset chips */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.75 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Tooltip title={helpText} arrow placement="top">
          <InfoIcon sx={{ fontSize: '0.95rem', color: 'text.secondary', opacity: 0.7, cursor: 'help' }} />
        </Tooltip>
        <Stack direction="row" spacing={0.75} sx={{ ml: 0.5, flexWrap: 'wrap', rowGap: 0.75 }}>
          {MODES.filter((m) => !(hideOnce && m.id === 'once')).map((m) => {
            const selected = mode === m.id;
            return (
              <Button
                key={m.id}
                size="small"
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => handleModeClick(m.id)}
                sx={(theme) => ({
                  minWidth: 0,
                  px: 1.25,
                  height: 26,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: '4px',
                  backgroundColor: selected
                    ? theme.palette.schedule?.daySelected || theme.palette.primary.main
                    : 'transparent',
                  color: selected
                    ? theme.palette.schedule?.dayText || theme.palette.primary.contrastText
                    : 'text.primary',
                  borderColor: selected
                    ? theme.palette.schedule?.daySelected || theme.palette.primary.main
                    : theme.palette.divider,
                  '&:hover': {
                    backgroundColor: selected
                      ? theme.palette.schedule?.daySelectedHover || theme.palette.primary.dark
                      : 'action.hover',
                  },
                })}
              >
                {m.label}
              </Button>
            );
          })}
        </Stack>
      </Stack>

      {/* Plain-English summary */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: mode === 'custom' ? 1.25 : 0 }}
      >
        {summary}
      </Typography>

      {/* Custom editor — only when needed */}
      {mode === 'custom' && (
        <Box
          sx={{
            mt: 0.5,
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px',
            backgroundColor: 'transparent',
          }}
        >
          {/* Frequency + day picker on one wrapping row */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ mb: 1.25, flexWrap: 'wrap', rowGap: 1 }}
          >
            <Typography variant="body2">Run every</Typography>
            <OutlinedInput
              type="number"
              value={freqCount}
              onChange={handleFreqCountChange}
              size="small"
              inputProps={{ min: 1, style: { padding: '6px 10px', width: 52 } }}
              sx={{ height: 30, '& fieldset': { borderColor: 'divider' } }}
            />
            <FormControl size="small">
              <Select
                value={freqUnit}
                onChange={handleFreqUnitChange}
                sx={{
                  height: 30,
                  '.MuiSelect-select': { py: 0.5 },
                  '& fieldset': { borderColor: 'divider' },
                }}
              >
                <MenuItem value="minutes">minutes</MenuItem>
                <MenuItem value="hours">hours</MenuItem>
                <MenuItem value="days">days</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ ml: 1 }}>on</Typography>
            <Stack direction="row" spacing={0.5}>
              {ALL_DAYS.map((day) => {
                const on = days.includes(day);
                const isWeekend = day === 'Saturday' || day === 'Sunday';
                return (
                  <Tooltip key={day} title={day} arrow>
                    <IconButton
                      onClick={() => handleDayToggle(day)}
                      size="small"
                      sx={(theme) => ({
                        width: 26,
                        height: 26,
                        borderRadius: '4px',
                        border: '1px solid',
                        borderColor: on
                          ? theme.palette.schedule?.daySelected || theme.palette.primary.main
                          : theme.palette.divider,
                        backgroundColor: on
                          ? theme.palette.schedule?.daySelected || theme.palette.primary.main
                          : 'transparent',
                        color: on
                          ? theme.palette.schedule?.dayText || theme.palette.primary.contrastText
                          : (isWeekend ? 'text.secondary' : 'text.primary'),
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: on
                            ? theme.palette.schedule?.daySelectedHover || theme.palette.primary.dark
                            : 'action.hover',
                        },
                      })}
                    >
                      {DAY_LETTERS[day]}
                    </IconButton>
                  </Tooltip>
                );
              })}
            </Stack>
            <Button
              size="small"
              variant="text"
              onClick={() => setMany({ days: [...ALL_DAYS] })}
              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 0.75 }}
            >
              All
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setMany({ days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] })}
              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 0.75 }}
            >
              Weekdays
            </Button>
          </Stack>

          {/* Optional time-of-day restriction */}
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Switch
              size="small"
              checked={restrictHours}
              onChange={handleRestrictToggle}
            />
            <Typography variant="body2" color="text.secondary">
              {restrictHours ? 'Only between' : 'Any time of day'}
            </Typography>
            {restrictHours && (
              <Stack direction="row" spacing={1} alignItems="center">
                <TimePicker
                  label="From"
                  value={parseTime(value?.start_time)}
                  onChange={(dt) => handleTimeChange('start_time', dt)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 120, '& .MuiOutlinedInput-root': { height: 30 } },
                    },
                  }}
                />
                <Typography variant="body2" color="text.secondary">to</Typography>
                <TimePicker
                  label="To"
                  value={parseTime(value?.end_time)}
                  onChange={(dt) => handleTimeChange('end_time', dt)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 120, '& .MuiOutlinedInput-root': { height: 30 } },
                    },
                  }}
                />
              </Stack>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
